import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { Direction8, PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind, holderClues, playersWithRole } from './util.ts';

/**
 * Cameras (GDD §8.4). The hacker turns 2–4 cameras so their cones miss the guards' path.
 * The hacker sees cones; the path is on the lookout's (or mastermind's) screen.
 */

export interface Cell {
  x: number;
  y: number;
}

export interface Cam {
  id: number;
  x: number;
  y: number;
  range: number;
}

export interface CamParams {
  size: number;
  cams: Cam[];
  path: Cell[];
  /** One orientation per camera that clears the path. */
  solution: Direction8[];
}

export interface CamState {
  orientation: Direction8[];
  failures: number;
  lockedUntilMs: number;
}

export const DIRS8: readonly Direction8[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
const VEC: Record<Direction8, Cell> = {
  n: { x: 0, y: -1 },
  ne: { x: 1, y: -1 },
  e: { x: 1, y: 0 },
  se: { x: 1, y: 1 },
  s: { x: 0, y: 1 },
  sw: { x: -1, y: 1 },
  w: { x: -1, y: 0 },
  nw: { x: -1, y: -1 },
};

const CAMS_BY_LEVEL = [2, 2, 2, 3, 3, 4] as const;
const PATH_BY_LEVEL = [6, 6, 8, 10, 12, 14] as const;
const SIZE = 7;

const params = (p: PuzzleInstance) => p.params as CamParams;
const state = (p: PuzzleInstance) => p.state as CamState;

/**
 * Does a camera facing `dir` see `cell`? 45° cone (±22.5°), integer arithmetic only:
 * cos²(22.5°) ≈ 0.8536, compared as 853/1000.
 */
export function covers(cam: Cam, dir: Direction8, cell: Cell): boolean {
  const vx = cell.x - cam.x;
  const vy = cell.y - cam.y;
  const dist2 = vx * vx + vy * vy;
  if (dist2 === 0 || dist2 > cam.range * cam.range) return false;
  const d = VEC[dir];
  const dot = vx * d.x + vy * d.y;
  if (dot <= 0) return false;
  const dlen2 = d.x * d.x + d.y * d.y;
  return dot * dot * 1000 >= 853 * dist2 * dlen2;
}

export function coveredCells(cam: Cam, dir: Direction8, size: number): Cell[] {
  const out: Cell[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) if (covers(cam, dir, { x, y })) out.push({ x, y });
  }
  return out;
}

export function pathCovered(par: CamParams, orient: readonly Direction8[]): boolean {
  return par.cams.some((cam, i) =>
    par.path.some((cell) => covers(cam, orient[i] as Direction8, cell)),
  );
}

function randomPath(rng: Rng, length: number): Cell[] | null {
  const start: Cell = { x: rng.int(0, SIZE - 1), y: rng.int(0, SIZE - 1) };
  const path = [start];
  const key = (c: Cell) => c.y * SIZE + c.x;
  const seen = new Set([key(start)]);
  const steps: Cell[] = [
    { x: 1, y: 0 },
    { x: -1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
  ];
  while (path.length < length) {
    const cur = path[path.length - 1] as Cell;
    const options = rng
      .shuffle(steps)
      .map((d) => ({ x: cur.x + d.x, y: cur.y + d.y }))
      .filter((c) => c.x >= 0 && c.y >= 0 && c.x < SIZE && c.y < SIZE && !seen.has(key(c)));
    const next = options[0];
    if (!next) return null;
    path.push(next);
    seen.add(key(next));
  }
  return path;
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const nCams = CAMS_BY_LEVEL[level] ?? 3;
  const length = PATH_BY_LEVEL[level] ?? 10;
  const range = level <= 2 ? 3 : 4;

  for (let attempt = 0; attempt < 400; attempt++) {
    const path = randomPath(rng, length);
    if (!path) continue;
    const onPath = new Set(path.map((c) => c.y * SIZE + c.x));
    const free: Cell[] = [];
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) if (!onPath.has(y * SIZE + x)) free.push({ x, y });
    }
    const spots = rng.shuffle(free).slice(0, nCams);
    if (spots.length < nCams) continue;
    const cams: Cam[] = spots.map((c, i) => ({ id: i, x: c.x, y: c.y, range }));

    const draft: CamParams = { size: SIZE, cams, path, solution: [] };
    const safe = cams.map((cam) => DIRS8.filter((d) => !path.some((cell) => covers(cam, d, cell))));
    if (safe.some((s) => s.length === 0)) continue;
    draft.solution = safe.map((s) => rng.pick(s));

    const initial = cams.map(() => rng.pick(DIRS8));
    if (!pathCovered(draft, initial)) continue;

    const s: CamState = { orientation: initial, failures: 0, lockedUntilMs: 0 };
    return {
      params: draft,
      state: s,
      clues: [
        {
          id: 'path',
          kind: 'camPath',
          holder: pickHolder(ctx.roles, ['lookout', 'mastermind']),
          value: path,
        },
      ],
      solvers: ['hacker'] as Role[],
    };
  }
  throw new Error('cameras generator failed');
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  const par = params(p);
  const st = state(p);
  if (act.kind !== 'cameras.rotate' && act.kind !== 'cameras.verify') {
    return failResult('invalid', 'not a camera action');
  }
  if (ctx.actorRole !== 'hacker') return failResult('notAllowed', 'only the hacker turns cameras');
  const audience = playersWithRole(ctx.players, 'hacker');
  if (act.kind === 'cameras.rotate') {
    if (!Number.isInteger(act.camera) || act.camera < 0 || act.camera >= par.cams.length) {
      return failResult('invalid', 'no such camera');
    }
    if (!DIRS8.includes(act.orientation)) return failResult('invalid', 'bad orientation');
    st.orientation[act.camera] = act.orientation;
    return okResult({
      events: [{ type: 'cameras.turn', audience, data: { camera: act.camera } }],
    });
  }
  if (ctx.execMs < st.lockedUntilMs) return failResult('cooldown', 'the network is locked');
  if (!pathCovered(par, st.orientation)) {
    return okResult({ solved: true, events: [{ type: 'cameras.clear', audience, data: {} }] });
  }
  st.failures += 1;
  let locked = false;
  const prevLock = st.lockedUntilMs;
  if (st.failures >= BALANCE.camerasMaxFailures) {
    st.lockedUntilMs = ctx.execMs + BALANCE.camerasLockMs;
    st.failures = 0;
    locked = true;
  }
  return okResult({
    errorTenths: BALANCE.penalty.cameras,
    undo: { kind: 'camerasFail', data: { prevLock, locked } },
    events: [{ type: 'cameras.seen', audience, data: { locked } }],
  });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'cameras',
    solved: p.solved,
    size: par.size,
    clues: holderClues(p, ctx.role),
  };
  if (ctx.role === 'hacker') {
    base['cams'] = par.cams.map((cam, i) => ({
      ...cam,
      orientation: st.orientation[i],
      cone: coveredCells(cam, st.orientation[i] as Direction8, par.size),
    }));
    base['failures'] = st.failures;
    base['lockedUntilMs'] = st.lockedUntilMs;
  } else {
    // The path holder sees the cameras' positions but not where they point.
    base['camPositions'] = par.cams.map((c) => ({ id: c.id, x: c.x, y: c.y, range: c.range }));
  }
  if (ctx.revealed) base['secret'] = { path: par.path, solution: par.solution };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  if (role !== 'hacker' || p.solved) return [];
  const out: PuzzleAct[] = [];
  for (const cam of params(p).cams) {
    for (const orientation of DIRS8)
      out.push({ kind: 'cameras.rotate', camera: cam.id, orientation });
  }
  out.push({ kind: 'cameras.verify' });
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  if (alterableClues(p, exclude).length === 0) return null;
  const clue = clueByKind(p, 'camPath');
  const path = (clue.value as Cell[]).map((c) => ({ ...c }));
  const i = rng.int(0, path.length - 1);
  const c = path[i] as Cell;
  const steps = [
    { x: 1, y: 0 },
    { x: -1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
  ]
    .map((d) => ({ x: c.x + d.x, y: c.y + d.y }))
    .filter((n) => n.x >= 0 && n.y >= 0 && n.x < SIZE && n.y < SIZE);
  // Prefer a cell the path does not already use, so the false path looks plausible.
  const fresh = steps.filter((n) => !path.some((q) => q.x === n.x && q.y === n.y));
  path[i] = rng.pick(fresh.length > 0 ? fresh : steps);
  return { clueId: clue.id, value: path };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as CamParams;
  const solvable = par.solution.length === par.cams.length && !pathCovered(par, par.solution);
  // Difficulty: how few orientations clear each camera (fewer = harder).
  const safeCounts = par.cams.map(
    (cam) => DIRS8.filter((d) => !par.path.some((cell) => covers(cam, d, cell))).length,
  );
  const space = safeCounts.reduce((a, b) => a * b, 1);
  return { solvable, difficulty: Math.round(8 ** par.cams.length / Math.max(1, space)) };
}

function autoSolve(p: PuzzleInstance): void {
  const par = params(p);
  state(p).orientation = par.solution.slice();
}

function undo(p: PuzzleInstance, u: { kind: string; data?: unknown }): void {
  if (u.kind !== 'camerasFail') return;
  const d = u.data as { prevLock: number; locked: boolean };
  const st = state(p);
  if (d.locked) {
    st.failures = BALANCE.camerasMaxFailures - 1;
    st.lockedUntilMs = d.prevLock;
  } else {
    st.failures = Math.max(0, st.failures - 1);
  }
}

export const camerasModule: PuzzleModule = {
  type: 'cameras',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo,
  solve,
};
